import { useTranslation } from 'react-i18next'
import { Link, Form } from 'react-router'
import { useSpinDelay } from 'spin-delay'
import { useIsPending } from '#app/utils/misc.tsx'
import {
	hasConsoleAccess,
	workspaceSurfacesFor,
} from '#app/utils/nav-surfaces.ts'
import { displayName, useUser } from '#app/utils/user.ts'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
} from './ui/dropdown-menu'
import { Icon } from './ui/icon'
import { Separator } from './ui/separator'
import { Spinner } from './ui/spinner'
import { UserAvatar } from './user-avatar'

/**
 * The logged-in user affordance, shared by the universal navbar (AppShell) on
 * every `full`/`marketing` surface. The trigger is a ghost pill — a ringed
 * {@link UserAvatar}, the display name, and a chevron that flips when the menu
 * opens — and the avatar is the way *into* the backend from anywhere: an
 * identity header (avatar + name + email) tops the menu, then it links to the
 * account hub, lists the user's *workspace* surfaces (the things they produce —
 * e.g. blog authoring) under a labelled section, and surfaces a Console door for
 * any user the surface registry grants console access (permission-derived,
 * ADR-084), so a partial-permission holder sees the way in and a plain user
 * never sees a dead link. Both the workspace list and the console door are pure
 * projections of the registry — nothing here is role-hardcoded.
 */
export function UserDropdown() {
	const { t } = useTranslation()
	const user = useUser()
	const workspaceSurfaces = workspaceSurfacesFor(user)
	const showConsole = hasConsoleAccess(user)
	const name = displayName(user)
	// Logout is a bare submit button inside a `<Form>` (it can't be a
	// `Button pending`), so the pending pattern is mirrored by hand: busy is
	// announced as soon as the `/logout` submission is in flight, while the
	// decorative spinner stays behind the shared spin-delay gate.
	const logoutPending = useIsPending({ formAction: '/logout' })
	const showLogoutSpinner = useSpinDelay(logoutPending, {
		delay: 400,
		minDuration: 300,
	})
	const logoutLabel = (
		<Icon className="text-body-md" name="exit">
			{t('actions.logout')}
		</Icon>
	)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					asChild
					variant="ghost"
					// Below `md` there is no hover to reveal the ghost, so the trigger
					// keeps a persistent subtle chip (border + `--bg` ramp fill) to read
					// as tappable; from `md` up the hover-reveal ghost is unchanged.
					className="max-md:border-border h-auto gap-2 px-2 py-1 max-md:border max-md:bg-[var(--bg-hover)]"
				>
					<Link
						to="/settings/profile"
						// this is for progressive enhancement
						onClick={(e) => e.preventDefault()}
						className="group flex items-center gap-2"
						aria-label={t('userMenu.label')}
					>
						{/* The `ring-1` is a persistent identity indicator on the trigger
						    only — the menu-header avatar below stays unringed (EPT-284 §6,
						    recorded so it isn't re-opened). */}
						<UserAvatar
							name={name}
							imageObjectKey={user.image?.objectKey}
							className="ring-border group-hover:ring-brand size-8 ring-1 transition-[box-shadow] duration-[var(--duration-fast)] ease-out motion-reduce:transition-none"
						/>
						<span className="text-body-sm font-bold">{name}</span>
						<Icon
							name="chevron-down"
							aria-hidden
							className="text-muted-foreground size-3.5 transition-transform duration-[var(--duration-fast)] ease-out group-data-[state=open]:rotate-180 motion-reduce:transition-none"
						/>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="end" className="w-57">
					<div className="flex items-center gap-2 px-2 py-1.5">
						<UserAvatar
							name={name}
							imageObjectKey={user.image?.objectKey}
							className="size-9"
						/>
						<div className="min-w-0">
							<p className="text-body-sm truncate font-bold">{name}</p>
							<p className="text-muted-foreground text-body-2xs truncate">
								{user.email}
							</p>
						</div>
					</div>
					<Separator className="my-1" />
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/settings/profile">
							<Icon className="text-body-md" name="avatar">
								{t('userMenu.account')}
							</Icon>
						</Link>
					</DropdownMenuItem>
					{workspaceSurfaces.length > 0 ? (
						<>
							<Separator className="my-1" />
							<p className="text-muted-foreground text-body-2xs px-2 py-1 font-semibold tracking-wide uppercase">
								{t('nav.workspace')}
							</p>
							{workspaceSurfaces.map((surface) => (
								<DropdownMenuItem key={surface.id} asChild>
									<Link prefetch="intent" to={surface.to}>
										<Icon className="text-body-md" name={surface.icon}>
											{t(surface.labelKey)}
										</Icon>
									</Link>
								</DropdownMenuItem>
							))}
							<Separator className="my-1" />
						</>
					) : null}
					{showConsole ? (
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to="/admin">
								<Icon className="text-body-md" name="laptop">
									{t('nav.console')}
								</Icon>
							</Link>
						</DropdownMenuItem>
					) : null}
					<Form action="/logout" method="POST">
						<DropdownMenuItem
							asChild
							// Keep the menu mounted while logout is in flight so the pending
							// feedback is actually visible; the post-logout revalidation
							// unmounts the whole dropdown anyway.
							onSelect={(event) => event.preventDefault()}
						>
							<button
								type="submit"
								className="relative w-full"
								// Mirror Button pending: block repeat submits while in flight.
								disabled={logoutPending}
								aria-busy={logoutPending || undefined}
							>
								{showLogoutSpinner ? (
									<>
										{/* Decorative — the button conveys busy via aria-busy; the
										    label stays in the a11y tree (opacity-0) so its name and
										    width are unchanged (mirrors the Button pending pattern). */}
										<span
											aria-hidden
											className="absolute inset-0 flex items-center justify-center"
										>
											<Spinner size="sm" />
										</span>
										<span className="opacity-0">{logoutLabel}</span>
									</>
								) : (
									logoutLabel
								)}
							</button>
						</DropdownMenuItem>
					</Form>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
